import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import confetti from 'canvas-confetti';
import useAppStore from '../store/useAppStore';
import { levels } from '../data/levels';
import { Level } from '../types';
import StarRating from '../components/StarRating';
import styles from './Result.module.css';

// 根据正确率计算星数
const calcStars = (correct: number, total: number): number => {
  if (total === 0) return 0;
  const rate = correct / total;
  if (rate >= 0.9) return 3;
  if (rate >= 0.7) return 2;
  if (rate >= 0.5) return 1;
  return 0;
};

const Result: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { score: number; total: number; levelId: string } | null;

  const completedLevels = useAppStore((s) => s.completedLevels);
  const lastLevel = completedLevels[completedLevels.length - 1];
  const wrongQuestions = useAppStore((s) => s.wrongQuestions);
  const checkInToday = useAppStore((s) => s.checkInToday);
  const currentTitle = useAppStore((s) => s.getCurrentTitle());
  const totalStars = useAppStore((s) => s.getTotalStars());
  const stageCompleted = useAppStore((s) => s.badgeAuxData.stageCompleted);

  const currentLevelId = state?.levelId || lastLevel?.levelId || '';
  const currentLevel = levels.find((l) => l.id === currentLevelId) as Level | undefined;
  const currentWrong = wrongQuestions.filter(
    (w) => w.levelId === currentLevelId
  );

  const score = state?.score ?? lastLevel?.score ?? 0;
  const total = state?.total ?? lastLevel?.totalCount ?? 5;
  const accuracy = total > 0 ? Math.round((score / total) * 100) : 0;
  const stars = calcStars(score, total);

  // 星级动画状态：依次点亮
  const [displayStars, setDisplayStars] = useState(0);

  useEffect(() => {
    // 签到
    checkInToday();

    // 星级动画
    if (stars > 0) {
      const t1 = setTimeout(() => setDisplayStars(1), 400);
      const t2 = setTimeout(() => { if (stars >= 2) setDisplayStars(2); }, 800);
      const t3 = setTimeout(() => { if (stars >= 3) setDisplayStars(3); }, 1200);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, [stars, checkInToday]);

  // Confetti 撒花
  useEffect(() => {
    const duration = 2000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: accuracy >= 90 ? 5 : accuracy >= 70 ? 3 : 2,
        angle: 60 + Math.random() * 60,
        spread: 60,
        origin: { x: Math.random(), y: 0.3 + Math.random() * 0.2 },
        colors: ['#7bc67e', '#f0c060', '#e8876b', '#c96a3a'],
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  }, [accuracy]);

  // 计算下一关
  const nextLevelId = useMemo(() => {
    const completedIds = new Set(completedLevels.map((cl) => cl.levelId));
    const cl = levels.find((l) => l.id === currentLevelId);
    if (!cl) return null;

    const sameModule = levels
      .filter((l) => l.stage === cl.stage && l.module === cl.module && l.order > cl.order)
      .sort((a, b) => a.order - b.order);
    const nextSame = sameModule.find((l) => !completedIds.has(l.id));
    if (nextSame) return nextSame.id;

    const moduleOrder = ['喂养', '睡眠', '日常护理', '安全防护', '发育观察'];
    const currentModuleIdx = moduleOrder.indexOf(cl.module);
    for (let i = currentModuleIdx + 1; i < moduleOrder.length; i++) {
      const first = levels.find(
        (l) => l.stage === cl.stage && l.module === moduleOrder[i] && l.order === 1
      );
      if (first && !completedIds.has(first.id)) return first.id;
    }
    return null;
  }, [currentLevelId, completedLevels]);

  const evaluation = (): string => {
    if (accuracy >= 90) return '太棒了！你已经很好地掌握了这部分知识';
    if (accuracy >= 70) return '不错！你已经基本理解核心内容';
    if (accuracy >= 50) return '还需要加强，建议复习薄弱知识点';
    return '需要重新学习，有些关键概念还没掌握';
  };

  // 分享文案
  const shareText = `🎓 育儿成长闯关 · 科学育儿学习平台\n\n我在「${currentLevel?.title || '关卡'}」中取得了正确率 ${accuracy}% ⭐${stars}/3 的成绩！\n\n一起来学科学育儿吧！`;

  const handleCopyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = shareText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const [copied, setCopied] = useState(false);

  const getTitleForStars = () => {
    if (stars === 3) return '完美通关！';
    if (stars === 2) return '表现不错！';
    if (stars === 1) return '继续加油！';
    return '还需努力！';
  };

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button className={styles.homeLink} onClick={() => navigate('/')}>
          ← 返回首页
        </button>
      </div>
      <div className={styles.resultCard}>
        <div className={styles.celebration}>🎉</div>
        <h1 className={styles.title}>{getTitleForStars()}</h1>

        {/* 星级展示 */}
        <div className={styles.starRow}>
          <StarRating stars={displayStars} size="large" animated />
        </div>

        <div className={styles.scoreRow}>
          <div className={styles.scoreCircle}>
            <span className={styles.scoreNum}>{score}</span>
            <span className={styles.scoreTotal}>/{total}</span>
          </div>
          <div className={styles.accuracyBadge}>{accuracy}%</div>
        </div>

        {/* 当前称号和总星数 */}
        <div className={styles.titleSummary}>
          {currentTitle} · 累计 ⭐ {totalStars} 颗
        </div>

        <div className={styles.evaluation}>
          <p>{evaluation()}</p>
        </div>

        {currentWrong.length > 0 && (
          <div className={styles.weakPoint}>
            <span className={styles.weakIcon}>⚠️</span>
            <span>薄弱点：{currentWrong.length} 道错题需要复习</span>
          </div>
        )}

        <div className={styles.actions}>
          {/* 阶段完成时，显示查看阶段总结按钮（用户主动点击，非自动跳转） */}
          {currentLevel && stageCompleted.includes(currentLevel.stage) && (
            <button
              className={styles.stageCompleteBtn}
              onClick={() => navigate('/stage-complete', { state: { stage: currentLevel.stage } })}
            >
              🏆 查看阶段总结 →
            </button>
          )}
          {/* 正确率低于50%时，推荐重新学习 */}
          {accuracy < 50 && currentLevel && (
            <button
              className={styles.retryBtn}
              onClick={() => navigate(`/level/${currentLevel.id}`)}
            >
              🔄 重新学习本关
            </button>
          )}
          <button className={styles.reviewBtn} onClick={() => navigate('/review')}>
            📚 加入复习中心
          </button>
          {nextLevelId ? (
            <button
              className={styles.nextBtn}
              onClick={() => navigate(`/level/${nextLevelId}`)}
            >
              下一关 →
            </button>
          ) : (
            <button className={styles.nextBtn} onClick={() => navigate('/map')}>
              返回学习地图 →
            </button>
          )}
        </div>

        {/* 分享区域 */}
        <div className={styles.shareSection}>
          <p className={styles.shareTitle}>💚 这类知识适合家人一起掌握</p>
          <div className={styles.shareCard}>
            <p className={styles.sharePreview}>
              我完成了「{currentLevel?.title || '关卡'}」 正确率 {accuracy}% ⭐{stars}/3
            </p>
            <button
              className={`${styles.copyBtn} ${copied ? styles.copied : ''}`}
              onClick={handleCopyShare}
            >
              {copied ? '✅ 已复制' : '📋 复制分享文案'}
            </button>
          </div>
          <button className={styles.familyLink} onClick={() => navigate('/family')}>
            了解家庭共学 →
          </button>
        </div>
      </div>
    </div>
  );
};

export default Result;
